import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Plus, Pencil, Trash2, X, Award, GripVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Toaster } from '../../components/ui/sonner';

const AdminDifferentials = () => {
  const { token } = useAuth();
  const [differentials, setDifferentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', icon: 'CheckCircle' });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const iconOptions = [
    'CheckCircle', 'Users', 'Shield', 'Wrench', 'CreditCard', 
    'Smartphone', 'MessageSquare', 'Award', 'Star', 'Heart'
  ];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/differentials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setDifferentials(await response.json());
    } catch (error) {
      toast.error('Erro ao carregar diferenciais');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingItem 
        ? `${API_URL}/api/admin/differentials/${editingItem.id}`
        : `${API_URL}/api/admin/differentials`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingItem ? 'Diferencial atualizado!' : 'Diferencial criado!');
        setShowModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja eliminar este diferencial?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/differentials/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Diferencial eliminado!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({ title: item.title, description: item.description, icon: item.icon });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ title: '', description: '', icon: 'CheckCircle' });
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Diferenciais</h1>
            <p className="text-gray-500">Gerencie os diferenciais da empresa</p>
          </div>
          <Button onClick={() => { setShowModal(true); resetForm(); }} className="bg-amber-600 hover:bg-amber-700">
            <Plus size={18} className="mr-2" />
            Novo Diferencial
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {differentials.map((item, index) => (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Award className="text-amber-600" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{item.title}</h3>
                        <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(item)}>
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {differentials.length === 0 && (
              <div className="col-span-2 text-center py-12 text-gray-500">Nenhum diferencial encontrado</div>
            )}
          </div>
        )}

        <p className="text-sm text-gray-500">Total: {differentials.length} diferenciais</p>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingItem ? 'Editar Diferencial' : 'Novo Diferencial'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}><X size={18} /></Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Título *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Ex: Equipe 100% Própria" required />
                </div>
                <div>
                  <Label>Descrição *</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} required />
                </div>
                <div>
                  <Label>Ícone</Label>
                  <select value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })} className="w-full h-10 px-3 border rounded-md">
                    {iconOptions.map(icon => <option key={icon} value={icon}>{icon}</option>)}
                  </select>
                </div>
                <div className="flex space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancelar</Button>
                  <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">{editingItem ? 'Atualizar' : 'Criar'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDifferentials;
